<template>
    <pendo-multiselect
        ref="multiselect"
        :value="value"
        :options="filteredOptions"
        :placeholder="multiselectConfig.placeholder"
        :min-menu-width="multiselectConfig.minMenuWidth"
        :value-key="multiselectConfig.valueKey"
        :label-key="multiselectConfig.labelKey"
        :close-on-select="multiselectConfig.closeOnSelect"
        :multiple="multiselectConfig.multiple"
        :allow-empty="multiselectConfig.allowEmpty"
        :group-options-key="multiselectConfig.groupOptionsKey"
        :group-label-key="multiselectConfig.groupLabelKey"
        :append-to-body="false"
        :searchable="false"
        full-width
        @input="handleInput"
        @closed="handleClosed"
        @open="handleOpen">
        <template #trigger="{ comboboxAttrs, handleKeydown, setTriggerRef }">
            <event-property-filter-tag
                :ref="setTriggerRef"
                v-bind="comboboxAttrs"
                :aria-label="$attrs['aria-label']"
                class="event-property-filter-tag-filter-multiselect"
                :class="{ 'filter-by-tag': isFilterByTag }"
                :tag-label="tagLabel"
                :icon-type="iconType"
                :is-open="isOpen"
                :disabled="disabled"
                :applicable="applicable"
                @click="toggleMenu"
                @keydown="handleKeydown" />
        </template>
        <template
            #header="{ comboboxAttrs, handleKeydown }"
            v-if="multiselectConfig.showSearch">
            <search
                v-bind="comboboxAttrs"
                v-model="search"
                class="options-search"
                data-cy="options-search"
                autofocus
                full-width
                :placeholder="$t('eventPropertyFilter.Search...')"
                @keydown="handleKeydown" />
        </template>
        <template #option="{ option }">
            <pendo-icon-option :option="option" />
        </template>
        <template #footer>
            <slot name="footer" />
        </template>
    </pendo-multiselect>
</template>

<script>
import { PendoMultiselect, PendoIconOption } from '@pendo/components';
import Search from '@/common/search/search.vue';
import EventPropertyFilterTag from './event-property-filter-tag.vue';

export default {
    name: 'TargetCardFilterMultiselect',
    components: {
        PendoMultiselect,
        Search,
        EventPropertyFilterTag,
        PendoIconOption,
    },
    props: {
        options: {
            type: Array,
            required: true,
        },
        multiselectConfig: {
            type: Object,
            default: () => ({}),
        },
        value: {
            type: Object,
            required: true,
        },
        valueLabel: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        applicable: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['open', 'closed', 'input'],
    data () {
        return {
            search: '',
            isOpen: false,
        };
    },
    computed: {
        isFilterByTag () {
            return this.tagLabel === this.multiselectConfig.placeholder;
        },
        filteredOptions () {
            const optionsWithIcons = this.options.map((option) => {
                const optionWithIconData = option;

                if (option.eventProps) {
                    optionWithIconData.eventProps = option.eventProps.map((eventProp) => {
                        return this.optionWithIcon(eventProp);
                    });
                }

                return optionWithIconData;
            });

            if (this.search) {
                if (optionsWithIcons.length && optionsWithIcons[0].eventProps) {
                    return optionsWithIcons.map((option) => {
                        return {
                            ...option,
                            eventProps: option.eventProps.filter((property) =>
                                property.eventPropertyName.toLowerCase().includes(this.search.toLowerCase()),
                            ),
                        };
                    });
                }

                return optionsWithIcons.filter((option) => {
                    if (option.name) {
                        return option.name.toLowerCase().includes(this.search.toLowerCase());
                    }
                    if (option.toLowerCase) {
                        return option.toLowerCase().includes(this.search.toLowerCase());
                    }

                    return false;
                });
            }

            return optionsWithIcons;
        },
        tagLabel () {
            return this.valueLabel || this.multiselectConfig.placeholder;
        },
        iconType () {
            const hasValueWithIconType = !this.isFilterByTag && !!this.value?.eventPropertySchema;

            return hasValueWithIconType ? this.optionWithIcon(this.value).icon.type : '';
        },
    },
    watch: {
        isOpen (newValue, oldValue) {
            if (newValue === oldValue) {
                return;
            }

            if (newValue) {
                this.$emit('open');
            } else {
                this.$emit('closed');
            }
        },
    },
    methods: {
        toggleMenu () {
            this.$refs.multiselect.toggleMenu();
        },
        // used by the parent component to focus the trigger
        // eslint-disable-next-line internal-rules/no-unused-properties
        getTriggerRef () {
            return this.$refs.multiselect.$refs.core.triggerRef;
        },
        handleInput ($event) {
            this.$emit('input', $event);
        },
        handleClosed () {
            this.isOpen = false;
        },
        handleOpen () {
            this.isOpen = true;
        },
        optionWithIcon (option) {
            let icon = '';

            switch (option.eventPropertySchema) {
                case 'string':
                    icon = { type: 'case-sensitive' };
                    break;
                case 'integer':
                case 'float':
                    icon = { type: 'hash' };
                    break;
                case 'boolean':
                    icon = { type: 'binary' };
                    break;
                case 'list':
                    icon = { type: 'list' };
                    break;
                case 'time':
                case 'date':
                    icon = { type: 'clock' };
                    break;
                default:
                    break;
            }

            return {
                ...option,
                icon,
            };
        },
    },
};
</script>

<style scoped lang="less">
.filter-by-tag {
    color: var(--palette-teal-90);
    background-color: rgba(var(--raw-palette-teal-70), 0.3);
    padding: 4px 8px;
}
.event-property-multiselect {
    .event-property-filter-tag-filter-multiselect {
        border-radius: 5px 0px 0px 5px;
    }
}
:deep(.pendo-multiselect) {
    &__header {
        border-bottom: 1px solid var(--palette-gray-40);
    }
}
:deep(.pendo-input) {
    &__control {
        padding: 8px;
    }
}
</style>
